/** In-window file preview (PDF / image / Office / SharePoint embed) — shared by the Files tab and the legal wizards.
 *  Never forces a download: PDFs and images render inline, Office files via Office Online, OneDrive via its embed page. */
import { Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function FilePreviewModal({ file, onClose }) {
  if (!file) return null;
  const proxyUrl = file.proxy_url ? (process.env.REACT_APP_BACKEND_URL || "") + file.proxy_url : null;
  const directUrl = file.url;
  // Stored DocuSign / DocuPilot links are pre-signed S3 URLs that expire — the proxy re-resolves them, so prefer it everywhere.
  const openUrl = proxyUrl || directUrl;
  // OneDrive feeds a separate short-lived CDN url for the iframe — `url`
  // stays as the durable web_url for the "Open in new tab" affordance.
  const previewUrl = file.preview_src || proxyUrl || directUrl;
  const name = file.name || "file";
  const ext = (name.split(".").pop() || "").toLowerCase();
  const isPdf = ext === "pdf";
  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext);
  const isOffice = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext);

  let iframeSrc = previewUrl;
  // `preview_kind: "embedded"` means the URL is already a complete viewer
  // page (e.g. SharePoint `_layouts/15/embed.aspx`) — DON'T wrap it in
  // Office Online or append #view=FitH; both would break the embed.
  if (file.preview_kind !== "embedded") {
    if (isPdf && previewUrl) {
      iframeSrc = previewUrl + (previewUrl.includes("#") ? "" : "#view=FitH&toolbar=1");
    } else if (isOffice && previewUrl) {
      // Office Online viewer — requires the URL to be publicly accessible.
      // Our proxy is publicly accessible (case_id-gated), so we can use it.
      iframeSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`;
    }
  }

  return (
    <Dialog open={!!file} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl w-[90vw] h-[90vh] p-0 sm:max-w-5xl flex flex-col" data-testid="customer-file-preview-modal">
        <DialogHeader className="px-4 py-2 border-b flex-shrink-0">
          <DialogTitle className="text-sm flex items-center gap-2 truncate">
            <Eye className="w-4 h-4 text-indigo-600 flex-shrink-0" />
            <span className="truncate">{name}</span>
            <Badge variant="outline" className="text-[10px] flex-shrink-0">{file.source}</Badge>
            {openUrl && (
              <a href={openUrl} target="_blank" rel="noreferrer" className="ml-auto mr-6 text-[11px] text-blue-600 hover:underline flex-shrink-0" data-testid="customer-file-preview-open-tab">
                Open in new tab ↗
              </a>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 bg-slate-50 overflow-hidden">
          {!previewUrl ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              No URL on file. Open <strong className="mx-1">{file.source}</strong> directly to view it.
            </div>
          ) : isImage ? (
            <div className="h-full overflow-auto flex items-center justify-center p-4">
              <img src={iframeSrc} alt={name} className="max-w-full max-h-full object-contain" />
            </div>
          ) : (
            <iframe
              title={name}
              src={iframeSrc}
              className="w-full h-full"
              style={{ border: 0 }}
              data-testid="customer-file-preview-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
